"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CircleIcon } from "lucide-react";

import { cn } from "./utils";

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("grid gap-3", className)}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        // Plain `border` (not `border-input`): `--input` resolves to
        // `transparent` in this theme's light mode (see RadioGroupTileItem's
        // own note below), so the global base-layer default
        // (`* { @apply border-border }`, a visible 10%-black line) is what
        // actually renders a boundary. `dark:border-input` matches Button's
        // outline variant, whose dark `--input` is a real, distinct grey.
        "text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:border-input dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className="fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

/**
 * A radio option rendered as a selectable tile/segmented button rather than
 * a dot-plus-label -- same `role="radio"`/`aria-checked` semantics as
 * `RadioGroupItem` (Radix sets both from the group's `value`), just styled
 * as the tile itself instead of a circle beside a `<label>`. Used where the
 * choices should read "at a glance" (Brad's portal feedback, 2026-10-02:
 * catalogue option tiles, not dropdowns) rather than as a vertical list.
 */
function RadioGroupTileItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-tile-item"
      className={cn(
        "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors outline-none",
        // Reviewer fix, 2026-10-02: plain `border` here, NOT `border-input`
        // -- `--input` resolves to `transparent` in this theme's light mode
        // (styles/theme.css), so an unselected tile with `border-input` has
        // no visible boundary at all and reads as plain text rather than a
        // choice. The base layer's `* { @apply border-border }` already
        // gives every bordered element a visible line by default (the same
        // thing Button's `outline` variant leans on); `dark:border-input`
        // mirrors that variant's dark-mode override, where `--input` is a
        // real, distinct grey rather than transparent.
        "border bg-background text-foreground hover:bg-muted/50 hover:border-ring/50 dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        // Selected state, reviewer fix: a solid `bg-primary` fill forces
        // every child to the hand-picked `text-primary-foreground` to stay
        // legible, and a muted secondary line (the ladder row's per-unit
        // price) hardcodes `text-muted-foreground` regardless, which reads
        // as near-invisible grey-on-pink. A light tint + pink border still
        // reads unmistakably "selected" without requiring every descendant
        // to override its own text colour.
        "data-[state=checked]:border-primary data-[state=checked]:bg-primary/10 data-[state=checked]:text-foreground data-[state=checked]:ring-2 data-[state=checked]:ring-primary/30",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem, RadioGroupTileItem };
